import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Column, IdType, TableState } from 'react-table';

import type { ElementBuilder } from '../../../../../../../../../../../common/_ForCommonLibrary/util/bem';
import { shouldDisableBufferOptIn } from '../../../../../../../../../../../utils/lineItem';
import type { LineItemEntity, MultipleEditDrawerLineItemsFormValues } from '../../../../../../../types';
import { getBufferColumns } from '../getBufferColumns';

type SelectedRows = Record<IdType<LineItemEntity>, boolean>;

type UseBufferTable = {
  data: LineItemEntity[];
  columns: Column<LineItemEntity>[];
  initialState?: Partial<TableState<LineItemEntity>>;
  listenSelectedRows: (selectedRows: SelectedRows) => void;
  selectedBufferToggles: SelectedRows;
  applyChangesToFormik: () => void;
};

export const useBufferTable = (
  element: ElementBuilder,
  entities: LineItemEntity[],
  isAutomated?: boolean
): UseBufferTable => {
  const { values, setFieldValue } = useFormikContext<MultipleEditDrawerLineItemsFormValues>();

  const isRowBufferOptInLocked = useCallback(
    (entity: LineItemEntity): boolean =>
      shouldDisableBufferOptIn({
        pacingType: entity.pacingType,
        ssp: entity.ssp,
        billableThirdParty: entity.billableThirdParty,
      }),
    []
  );

  const initialBufferValues = useMemo(() => {
    const bufferMap: Record<string, number | null | undefined> = {};
    entities.forEach((entity) => {
      bufferMap[entity.id] = entity.deliveryBufferPercent;
    });
    return bufferMap;
  }, [entities]);

  const [localBufferValues, setLocalBufferValues] = useState(initialBufferValues);

  useEffect(() => {
    setLocalBufferValues(initialBufferValues);
  }, [initialBufferValues]);

  const entityIndexMap = useMemo(() => {
    const map: Record<string, number> = {};

    values.entities.forEach((entity, index) => {
      map[entity.id] = index;
    });

    return map;
  }, [values.entities]);

  const onSubmitBuffer = useCallback((val: number, entityId: string): void => {
    setLocalBufferValues((prev) => ({
      ...prev,
      [entityId]: val,
    }));
  }, []);

  const columns = useMemo(
    () =>
      getBufferColumns({
        cellClassName: element('cell'),
        onSubmitBuffer,
        localBufferValues,
      }),
    [element, onSubmitBuffer, localBufferValues]
  );

  const [initialState] = useState<Partial<TableState<LineItemEntity>> | undefined>(() => {
    if (!isAutomated) return undefined;

    const selectedRowIds: Record<string, boolean> = {};
    entities.forEach((entity) => {
      if (!isRowBufferOptInLocked(entity)) {
        selectedRowIds[entity.id] = true;
      }
    });

    return { selectedRowIds };
  });

  const [selectedBufferToggles, setSelectedBufferToggles] = useState<SelectedRows>(() => {
    if (!isAutomated) return {};

    const selectedRowIds: Record<string, boolean> = {};
    entities.forEach((entity) => {
      if (!isRowBufferOptInLocked(entity)) {
        selectedRowIds[entity.id] = true;
      }
    });

    return selectedRowIds;
  });

  const listenSelectedRows = useCallback((selectedRows: SelectedRows): void => {
    setSelectedBufferToggles(selectedRows);
  }, []);

  const applyChangesToFormik = useCallback((): void => {
    entities.forEach((entity) => {
      const entityIndex = entityIndexMap[entity.id];
      if (entityIndex === undefined) return;

      const isSelected = isRowBufferOptInLocked(entity) ? false : selectedBufferToggles[entity.id] || false;

      setFieldValue(`entities[${entityIndex}].automateBuffer`, isSelected);

      const localValue = localBufferValues[entity.id];
      if (localValue !== undefined && localValue !== entity.deliveryBufferPercent) {
        setFieldValue(`entities[${entityIndex}].deliveryBufferPercent`, localValue);
      }
    });
  }, [entities, entityIndexMap, selectedBufferToggles, localBufferValues, setFieldValue, isRowBufferOptInLocked]);

  return { data: entities, columns, initialState, listenSelectedRows, selectedBufferToggles, applyChangesToFormik };
};
